/**
 * Subscription-Plans API.
 *
 * Backend: admin-api auf api.it-sce.de unter `/admin/sync/subscription-plans`
 * (proxy zu erpnext-sync). Antworten kommen entweder direkt als Array oder
 * als AdminEnvelope `{success, data}` — wir akzeptieren beides defensiv.
 */
import type { AxiosInstance } from 'axios'
import { request } from '../client'
import type {
  TSubscriptionPlan,
  TSubscriptionPlanList,
  TSubscriptionPlanVisibilityRequest,
  TSubscriptionPlanVariantRequest,
  TSubscriptionPlanItemRequest,
  TSubscriptionPlansStats,
} from '../types/staff'

interface AdminEnvelope<T> {
  success?: boolean
  data?: T
  error?: string
}

function unwrap<T>(payload: AdminEnvelope<T> | T): T {
  if (payload && typeof payload === 'object' && 'data' in (payload as object)) {
    return (payload as AdminEnvelope<T>).data as T
  }
  return payload as T
}

export interface SubscriptionPlansApi {
  list: (params?: { type?: string }) => Promise<TSubscriptionPlanList>
  stats: () => Promise<TSubscriptionPlansStats>
  setVisibility: (id: number, req: TSubscriptionPlanVisibilityRequest) => Promise<TSubscriptionPlan>
  setVariant: (id: number, req: TSubscriptionPlanVariantRequest) => Promise<TSubscriptionPlan>
  setItem: (id: number, req: TSubscriptionPlanItemRequest) => Promise<TSubscriptionPlan>
  triggerImport: () => Promise<{ success: boolean; created?: number; updated?: number }>
}

export function createSubscriptionPlansApi(api: AxiosInstance): SubscriptionPlansApi {
  const base = '/admin/sync/subscription-plans'
  return {
    list: async (params) => {
      const res = await request<AdminEnvelope<TSubscriptionPlan[]> | TSubscriptionPlan[]>(api, {
        method: 'GET', url: base, params,
      })
      const arr = unwrap<TSubscriptionPlan[]>(res)
      return { plans: Array.isArray(arr) ? arr : [], total: Array.isArray(arr) ? arr.length : 0 }
    },
    stats: async () => {
      const res = await request<AdminEnvelope<TSubscriptionPlansStats> | TSubscriptionPlansStats>(api, {
        method: 'GET', url: `${base}/stats`,
      })
      return unwrap<TSubscriptionPlansStats>(res)
    },
    setVisibility: async (id, req) => {
      const res = await request<AdminEnvelope<TSubscriptionPlan> | TSubscriptionPlan>(api, {
        method: 'PUT', url: `${base}/${id}/visibility`, data: req,
      })
      return unwrap<TSubscriptionPlan>(res)
    },
    setVariant: async (id, req) => {
      const res = await request<AdminEnvelope<TSubscriptionPlan> | TSubscriptionPlan>(api, {
        method: 'PUT', url: `${base}/${id}/variant`, data: req,
      })
      return unwrap<TSubscriptionPlan>(res)
    },
    setItem: async (id, req) => {
      const res = await request<AdminEnvelope<TSubscriptionPlan> | TSubscriptionPlan>(api, {
        method: 'PUT', url: `${base}/${id}/item`, data: req,
      })
      return unwrap<TSubscriptionPlan>(res)
    },
    triggerImport: async () => {
      const res = await request<AdminEnvelope<{ created?: number; updated?: number }> | { created?: number; updated?: number }>(api, {
        method: 'POST', url: `${base}/import`,
      })
      const data = unwrap(res) as { created?: number; updated?: number }
      return { success: true, ...data }
    },
  }
}
